import { Tooltip } from '@material-ui/core';
import React, { ReactNode, useEffect, useState } from 'react';

import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { FormBuilderTextInput } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/FormBuilderTextInput';
import { IncludeExcludeToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/IncludeExcludeToggle';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { Col, Cols, spacing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { EAllowStatus } from '~/src/dash/utils/options';

const convertStatus = (
  object: Record<string, any>,
  allowKey?: string,
  isAllowTypeBool?: boolean,
): EAllowStatus | undefined => {
  if (allowKey) {
    if (isAllowTypeBool) {
      return object[allowKey] !== false ? EAllowStatus.ALLOW : EAllowStatus.DENY;
    } else {
      return object[allowKey] as EAllowStatus;
    }
  }
  return undefined;
};

export interface ITextInputWithIncludeProps {
  placeholder?: string;
  valueKey: string;
  allowKey?: string;
  tooltip?: string | NonNullable<ReactNode>;
  key: string; // MUST BE UNIQUE
  isAllowTypeBool?: boolean; // default is EBool
}

export const TextInputWithInclude = ({
  placeholder,
  valueKey,
  allowKey,
  tooltip = '',
  isAllowTypeBool = false,
}: ITextInputWithIncludeProps) => {
  const { object, defaultObject, maintainEditorValuesOnUnmount } = useFormBuilderStore();

  const convertedStatus = convertStatus(object, allowKey, isAllowTypeBool);

  const [input, setInput] = useState<string>(object[valueKey]);
  const [status, setStatus] = useState<EAllowStatus | undefined>(convertedStatus);

  useEffect(() => {
    if (maintainEditorValuesOnUnmount) return;
    return () => {
      object[valueKey] = defaultObject[valueKey];
      if (allowKey) object[allowKey] = defaultObject[allowKey];
    };
  }, [maintainEditorValuesOnUnmount]);

  const handleTextChange = (text: string) => {
    setInput(text);
    object[valueKey] = text;
  };

  const handleToggleStatus = () => {
    if (allowKey) {
      const newStatus = status === EAllowStatus.ALLOW ? EAllowStatus.DENY : EAllowStatus.ALLOW;
      if (isAllowTypeBool) {
        object[allowKey] = newStatus === EAllowStatus.ALLOW;
      } else {
        object[allowKey] = newStatus;
      }
      setStatus(newStatus);
    }
  };

  return (
    <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
      <Col flex style={{ maxWidth: '480px' }}>
        <FormBuilderTextInput
          placeholder={placeholder || 'Enter Data'}
          initialValue={input}
          handleOnChange={handleTextChange}
          key={valueKey}
          fullWidth
          inputID={valueKey}
        />
      </Col>
      {tooltip && (
        <Col>
          <Tooltip title={tooltip} placement='top' interactive>
            <div style={{ display: 'flex' }}>
              <IconV2 name='info-static' color={publicaColors.gray} />
            </div>
          </Tooltip>
        </Col>
      )}
      {allowKey && status && (
        <Col>
          <IncludeExcludeToggle
            status={status}
            handleToggleStatus={handleToggleStatus}
            key={allowKey}
          />
        </Col>
      )}
    </Cols>
  );
};
